import { Card, Skeleton } from "antd";
import dayjs from "dayjs";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type AttendanceTrendProps = {
  attendanceTrendData?: {
    data: {
      total_employees: number;
      daily_stats: {
        date: string;
        present_count: number;
        on_leave_count: number;
        absent_count: number;
        no_data: number;
      }[];
    };
  };
  isLoading?: boolean;
};

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;

  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0F172A] text-white p-4 rounded-xl shadow-xl border border-[#1f2d40] ">
        <p className="text-center text-white text-lg font-normal mb-2">
          {String(label)}
        </p>

        <div className="flex justify-start items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-full text-white text-xs font-normal bg-[#2CA2C1]" />
            Present:{" "}
            {String(
              payload.find((p: any) => p.dataKey === "present")?.value || 0
            )}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-full text-white text-xs font-normal bg-[#954A00]" />
            Absent:{" "}
            {String(
              payload.find((p: any) => p.dataKey === "absent")?.value || 0
            )}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-full text-white text-xs font-normal bg-[#FFC300]" />
            Leave:{" "}
            {String(
              payload.find((p: any) => p.dataKey === "leave")?.value || 0
            )}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-full text-white text-xs font-normal bg-[#6B7280]" />
            No Data:{" "}
            {String(
              payload.find((p: any) => p.dataKey === "no_data")?.value || 0
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default function AttendanceTrend({
  attendanceTrendData,
  isLoading,
}: AttendanceTrendProps) {
  // Transform API data to chart format
  const chartData =
    attendanceTrendData?.data?.daily_stats?.map((stat) => ({
      name: dayjs(stat.date).format("MMM DD"),
      present: stat.present_count,
      absent: stat.absent_count,
      leave: stat.on_leave_count,
      no_data: stat.no_data,
    })) || [];

  // Show message when no data is available
  if (!isLoading && chartData.length === 0) {
    return (
      <Card className="text-white p-2 rounded-2xl shadow-xl dark:bg-theme-slate-800 border dark:border-theme-gray-700">
        <div className="flex justify-center items-center h-[345px]">
          <div className="text-center">
            <div className="text-white text-lg md:text-3xl font-bold mb-2">
              Attendance Trends
            </div>
            <div className="text-gray-400 text-sm">
              No attendance data available for the selected period
            </div>
          </div>
        </div>
      </Card>
    );
  }
  if (isLoading) {
    return (
      <Card className=" text-white p-2 h-full rounded-2xl shadow-xl  dark:bg-theme-slate-800 border dark:border-theme-gray-700">
        <div className="flex justify-between items-center mb-3">
          <Skeleton.Input active size="small" className="w-48" />
          <div className="flex justify-start items-center gap-3">
            <Skeleton.Button active size="small" />
            <Skeleton.Button active size="small" />
            <Skeleton.Button active size="small" />
          </div>
        </div>
        {/* <Skeleton.Node active className="w-full h-[345px]" /> */}
      </Card>
    );
  }

  return (
    <>
      <Card className=" text-white p-2 rounded-2xl shadow-xl  dark:bg-theme-slate-800 border dark:border-theme-gray-700">
        <div className="flex justify-between items-center mb-3">
          <div className="justify-start text-white text-lg md:text-3xl font-bold">
            Attendance Trends
          </div>
          <div className="flex justify-start items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-md bg-[#2CA2C1]"></span> Present
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-md bg-[#954A00]"></span> Absent
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-md bg-[#FFC300]"></span> Leave
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-md bg-[#6B7280]"></span> No Data
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={345}>
          <AreaChart
            data={chartData}
            margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#21495f" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#1c334b" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorAbsent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#5e4834" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#35323b" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorLeave" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6d5a2b" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#252b3d" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorNoData" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4a5568" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#2d3748" stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#1e2a38" />
            <XAxis dataKey="name" stroke="#ffffff" />
            <YAxis stroke="#ffffff" />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: "#d1d5db", strokeWidth: 1 }}
            />

            <Area
              type="basis"
              dataKey="present"
              stroke="#2CA2C1"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorPresent)"
            />

            <Area
              type="basis"
              dataKey="absent"
              stroke="#954A00"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorAbsent)"
            />

            <Area
              type="basis"
              dataKey="leave"
              stroke="#FFC300"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorLeave)"
            />

            <Area
              type="basis"
              dataKey="no_data"
              stroke="#6B7280"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorNoData)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    </>
  );
}
